import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Bike, Check, ClipboardList, LogOut, MapPin, Plus, Trash2, X } from "lucide-react";
import { LogoMark } from "@/App";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];
const statusClass = { "Request received": "st-new", Confirmed: "st-confirmed", Completed: "st-done", Cancelled: "st-cancelled" };

export default function Crew() {
  const [token, setToken] = useState(() => sessionStorage.getItem("crew_token") || "");
  const [pin, setPin] = useState("");
  const [loginError, setLoginError] = useState("");
  const [bookings, setBookings] = useState(null);
  const [areas, setAreas] = useState([]);
  const [newArea, setNewArea] = useState("");
  const [areaError, setAreaError] = useState("");
  const [capacity, setCapacity] = useState(5);
  const [capacityInput, setCapacityInput] = useState("5");
  const [capacityMsg, setCapacityMsg] = useState("");

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadData = useCallback(async () => {
    try {
      const [bookingsRes, areasRes, settingsRes] = await Promise.all([
        axios.get(`${API}/crew/bookings`, auth()),
        axios.get(`${API}/pickup-areas`),
        axios.get(`${API}/crew/settings`, auth()),
      ]);
      setBookings(bookingsRes.data);
      setAreas(areasRes.data);
      setCapacity(settingsRes.data.daily_capacity);
      setCapacityInput(String(settingsRes.data.daily_capacity));
    } catch (error) {
      if (error.response?.status === 401) { sessionStorage.removeItem("crew_token"); setToken(""); }
    }
  }, [auth]);

  useEffect(() => { if (token) loadData(); }, [token, loadData]);

  const login = async (event) => {
    event.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/crew/login`, { pin });
      sessionStorage.setItem("crew_token", data.token);
      setToken(data.token);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Could not sign in. Please try again.");
    }
  };

  const logout = () => { sessionStorage.removeItem("crew_token"); setToken(""); setBookings(null); };

  const setStatus = async (referenceId, status) => {
    await axios.patch(`${API}/crew/bookings/${referenceId}`, { status }, auth());
    loadData();
  };

  const addArea = async (event) => {
    event.preventDefault();
    setAreaError("");
    if (!newArea.trim()) return;
    try {
      await axios.post(`${API}/crew/pickup-areas`, { name: newArea }, auth());
      setNewArea("");
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setAreaError(typeof detail === "string" ? detail : "Could not add that area.");
    }
  };

  const removeArea = async (name) => {
    await axios.delete(`${API}/crew/pickup-areas/${encodeURIComponent(name)}`, auth());
    loadData();
  };

  const saveCapacity = async (event) => {
    event.preventDefault();
    setCapacityMsg("");
    try {
      const { data } = await axios.put(`${API}/crew/settings`, { daily_capacity: Number(capacityInput) }, auth());
      setCapacity(data.daily_capacity);
      setCapacityInput(String(data.daily_capacity));
      setCapacityMsg(`Fleet size updated — ${data.daily_capacity} scooters per day from now on.`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setCapacityMsg(Array.isArray(detail) ? detail[0]?.msg : typeof detail === "string" ? detail : "Could not update the fleet size.");
    }
  };

  if (!token) {
    return (
      <div className="crew-login" data-testid="crew-login-page">
        <motion.form className="crew-login-card" onSubmit={login} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} data-testid="crew-login-form">
          <LogoMark size={44} />
          <h1>Ride_On crew</h1>
          <p>Enter the crew PIN to manage booking requests and pickup areas.</p>
          {loginError && <div className="form-error" role="alert" data-testid="crew-login-error">{loginError}</div>}
          <input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Crew PIN" required data-testid="crew-pin-input" />
          <button type="submit" data-testid="crew-login-button">Open dashboard <span>→</span></button>
          <a href="/" className="crew-back" data-testid="crew-back-home">← Back to the site</a>
        </motion.form>
      </div>
    );
  }

  const counts = bookings ? {
    total: bookings.length,
    open: bookings.filter((b) => b.status === "Request received").length,
    confirmed: bookings.filter((b) => b.status === "Confirmed").length,
  } : { total: 0, open: 0, confirmed: 0 };

  return (
    <div className="crew-shell" data-testid="crew-dashboard">
      <header className="crew-header">
        <a className="brand" href="/" data-testid="crew-brand"><LogoMark /><span>Ride<span>_On</span></span></a>
        <div className="crew-header-right">
          <span className="crew-tag">CREW DASHBOARD</span>
          <button onClick={logout} data-testid="crew-logout-button"><LogOut size={15} /> Sign out</button>
        </div>
      </header>
      <main className="crew-main">
        <section className="crew-stats" data-testid="crew-stats">
          <div><b data-testid="stat-total">{counts.total}</b><span>Total requests</span></div>
          <div><b data-testid="stat-open">{counts.open}</b><span>Awaiting reply</span></div>
          <div><b data-testid="stat-confirmed">{counts.confirmed}</b><span>Confirmed</span></div>
        </section>
        <section className="crew-section" data-testid="crew-bookings-section">
          <h2><ClipboardList size={18} /> Booking requests</h2>
          {bookings === null ? <p className="crew-muted">Loading…</p> : bookings.length === 0 ? <p className="crew-muted" data-testid="no-bookings">No booking requests yet. New requests from the site appear here.</p> : (
            <div className="crew-bookings">
              {bookings.map((b) => (
                <article className="crew-booking" key={b.reference_id} data-testid={`booking-row-${b.reference_id}`}>
                  <div className="crew-booking-top">
                    <strong>{b.customer_name}</strong>
                    <span className={`status-pill ${statusClass[b.status] || ""}`} data-testid={`status-pill-${b.reference_id}`}>{b.status}</span>
                  </div>
                  <div className="crew-booking-meta">
                    <span data-testid={`ref-${b.reference_id}`}>{b.reference_id}</span>
                    {b.slot && b.status !== "Cancelled" && <span className="slot-chip" data-testid={`slot-${b.reference_id}`}>Slot {b.slot} of {capacity}</span>}
                    <span>{b.scooter_name}</span>
                    <span>{b.pickup_area}</span>
                    <span>{b.pickup_date} · {b.pickup_time} · {b.duration}</span>
                    <a href={`tel:${b.phone}`}>{b.phone}</a>
                  </div>
                  {b.notes && <p className="crew-notes">“{b.notes}”</p>}
                  <div className="crew-actions">
                    {b.status === "Request received" && <button onClick={() => setStatus(b.reference_id, "Confirmed")} data-testid={`confirm-${b.reference_id}`}><Check size={14} /> Confirm</button>}
                    {(b.status === "Request received" || b.status === "Confirmed") && <button className="ghost" onClick={() => setStatus(b.reference_id, "Completed")} data-testid={`complete-${b.reference_id}`}>Completed</button>}
                    {b.status !== "Cancelled" && b.status !== "Completed" && <button className="danger" onClick={() => setStatus(b.reference_id, "Cancelled")} data-testid={`cancel-${b.reference_id}`}><X size={14} /> Cancel</button>}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
        <section className="crew-section" data-testid="crew-fleet-section">
          <h2><Bike size={18} /> Fleet size</h2>
          <p className="crew-muted">How many scooters can be booked per day. Right now: <strong data-testid="fleet-capacity-current">{capacity} scooters daily</strong>.</p>
          <form className="area-add" onSubmit={saveCapacity}>
            <input type="number" min="1" max="20" value={capacityInput} onChange={(e) => setCapacityInput(e.target.value)} data-testid="fleet-capacity-input" aria-label="Scooters per day" />
            <button type="submit" data-testid="fleet-capacity-save">Save fleet size</button>
          </form>
          {capacityMsg && <p className="crew-muted capacity-msg" data-testid="fleet-capacity-msg">{capacityMsg}</p>}
        </section>
        <section className="crew-section" data-testid="crew-areas-section">
          <h2><MapPin size={18} /> Pickup areas</h2>
          <p className="crew-muted">These areas appear in the booking form on the site. Add your custom Sri Vijayapuram pickup points here.</p>
          <div className="crew-areas">
            {areas.map((name) => (
              <span className="area-chip" key={name} data-testid={`area-chip-${name.toLowerCase().replaceAll(" ", "-")}`}>{name}<button onClick={() => removeArea(name)} aria-label={`Remove ${name}`} data-testid={`area-delete-${name.toLowerCase().replaceAll(" ", "-")}`}><Trash2 size={12} /></button></span>
            ))}
          </div>
          <form className="area-add" onSubmit={addArea}>
            <input value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="Add a new pickup area…" data-testid="area-input" />
            <button type="submit" data-testid="area-add-button"><Plus size={15} /> Add area</button>
          </form>
          {areaError && <div className="form-error" role="alert" data-testid="area-error">{areaError}</div>}
        </section>
      </main>
    </div>
  );
}
